import { useFormContext } from 'react-hook-form';
import { Group } from '../../types';
import { FormCard } from '@portal_components/components/FormCard';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import React, { useEffect, useMemo } from 'react';
import { FormRadioButtonGroup } from '@portal_components/components/FormRadioButtonGroup';
import { Checkbox } from '@portal_components/components/Checkbox';
import { FormSearchDropdown } from '@portal_components/components/FormSearchDropdown';

const SINGLE_SITES_GROUP = '1';
const GROUP_CONTACT_GROUP = '3';
const DIRECT_RESELLER_GROUP = '4';
const COMMISSION_ONLY_RESELLER_GROUP = '5';

interface RelatedGroupsProps {
  customerGroups: { customerGroupName: string; customerGroupId: string; customerGroupTypeId: string }[];
  readonly: boolean;
}

export const RelatedGroups = ({ customerGroups, readonly }: RelatedGroupsProps) => {
  const { watch, setValue } = useFormContext<Group>();

  const [customerGroupTypeId, relationshipType] = watch([
    'customerGroupTypeId',
    'relatedGroup.relationshipType'
  ]);

  useEffect(() => {
    // do nothing if it's a valid combination
    if (
      customerGroupTypeId === SINGLE_SITES_GROUP &&
      relationshipType &&
      [DIRECT_RESELLER_GROUP, COMMISSION_ONLY_RESELLER_GROUP].includes(relationshipType)
    )
      return;
    if (customerGroupTypeId === GROUP_CONTACT_GROUP && relationshipType === GROUP_CONTACT_GROUP) return;
    setValue('relatedGroup.relationshipType', undefined);
    setValue('relatedGroup.customerRelatedGroupId', undefined);
  }, [customerGroupTypeId, relationshipType, setValue]);

  const directResellerGroups = useMemo(() => {
    try {
      return customerGroups
        .filter(group => group.customerGroupTypeId === DIRECT_RESELLER_GROUP)
        .map(group => ({ title: group.customerGroupName, value: group.customerGroupId }));
    } catch (error) {
      return [];
    }
  }, [customerGroups]);

  const commissionOnlyGroups = useMemo(() => {
    return customerGroups
      .filter(group => group.customerGroupTypeId === COMMISSION_ONLY_RESELLER_GROUP)
      .map(group => ({ title: group.customerGroupName, value: group.customerGroupId }));
  }, [customerGroups]);

  const handleLinkAdditionalGroupCheckex = (checked: boolean) => {
    if (checked) {
      setValue('relatedGroup.relationshipType', GROUP_CONTACT_GROUP);
    } else {
      setValue('relatedGroup.relationshipType', undefined);
    }
  };

  if (!customerGroupTypeId || ![SINGLE_SITES_GROUP, GROUP_CONTACT_GROUP].includes(customerGroupTypeId))
    return null;

  return (
    <FormCard columns={2}>
      {customerGroupTypeId === SINGLE_SITES_GROUP && (
        <FormRadioButtonGroup
          title="Relationship Type"
          name="relatedGroup.relationshipType"
          buttons={[
            { title: 'Direct Reseller Billing', value: DIRECT_RESELLER_GROUP },
            { title: 'Commission Only', value: COMMISSION_ONLY_RESELLER_GROUP },
            { title: 'None', value: undefined }
          ]}
          readonly={readonly}
        />
      )}
      {customerGroupTypeId === SINGLE_SITES_GROUP && relationshipType === DIRECT_RESELLER_GROUP && (
        <FormDropdown<Group>
          title="Reseller (Direct Reseller Billing)"
          name="relatedGroup.customerRelatedGroupId"
          placeholder="Select..."
          elementsForDisplay={directResellerGroups}
          readonly={readonly}
        />
      )}
      {customerGroupTypeId === SINGLE_SITES_GROUP && relationshipType === COMMISSION_ONLY_RESELLER_GROUP && (
        <FormDropdown<Group>
          title="Reseller (Commission Only)"
          name="relatedGroup.customerRelatedGroupId"
          placeholder="Select..."
          elementsForDisplay={commissionOnlyGroups}
          readonly={readonly}
        />
      )}
      {customerGroupTypeId === GROUP_CONTACT_GROUP && (
        <Checkbox
          title="Link another related group"
          checked={relationshipType === GROUP_CONTACT_GROUP}
          readonly={readonly}
          onSelected={handleLinkAdditionalGroupCheckex}
        />
      )}
      {customerGroupTypeId === GROUP_CONTACT_GROUP && relationshipType === GROUP_CONTACT_GROUP && (
        <FormSearchDropdown<Group>
          title="Related Group"
          name="relatedGroup.customerRelatedGroupId"
          placeholder="Select..."
          elementsForDisplay={customerGroups.map(group => ({
            title: group.customerGroupName,
            value: group.customerGroupId
          }))}
          readonly={readonly}
        />
      )}
    </FormCard>
  );
};
